import './bootstrap';
import '../css/app.css';
import 'flowbite';

import { createRoot } from 'react-dom/client';
import { createInertiaApp, Link } from '@inertiajs/react';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import ReactGA from 'react-ga4';
import React from 'react';
import ErrorBoundary from './ErrorBoundary';
import logClientError from './logClientError';
import { toast } from 'react-toastify';
import axios from 'axios';

import { InertiaProgress } from '@inertiajs/progress';

InertiaProgress.init({
  color: '#4B5563',
  showSpinner: true,
  delay: 250,
});

// Global error capture for synchronous errors
window.onerror = function (message, source, lineno, colno, error) {
  logClientError({
    type: 'error',
    message,
    source,
    lineno,
    colno,
    stack: error?.stack || null,
  });
};

window.onunhandledrejection = function (event) {
  let message = event.reason?.message || event.reason;

  // Ignore 419 errors (Laravel session expired)
  if (message.includes('419') || message.includes('CSRF token mismatch')) {
    return;
  }

  // Ignore dynamically imported module errors
  if (!message.includes('dynamically imported module')) {
    logClientError({
      type: 'unhandledrejection',
      message: message,
      stack: event.reason?.stack || null,
    });
  }
};

window.addEventListener('beforeunload', () => {
  sessionStorage.setItem('previousUrl', window.location.href);
});

if (import.meta.env.MODE === 'production') {
  ReactGA.initialize('G-NS62EEEW6K'); // Replace with your GA4 measurement ID
  // Track the initial page load
  ReactGA.send({ hitType: 'pageview', page: window.location.pathname + window.location.search });
}

/*
new PerformanceObserver((entryList) => {
  for (const entry of entryList.getEntries()) {
    console.log('Layout shift:', entry);
    if (entry.value > 0.5) {
      debugger;
    }
  }
}).observe({ type: 'layout-shift', buffered: true });
*/

// Add axios interceptor to handle 429 errors
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 429) {
      toast.error(
        <div>
          <b>You've reached the limit for this feature.</b>
          <br />
          To ensure fair usage, we limit access to some of our AI features for free users. You can
          try again in a few minutes, or{' '}
          <Link className="underline text-blue-500 dark:text-blue-400" href="/unlock">
            click here
          </Link>{' '}
          to explore premium plans and enjoy unlimited access.
        </div>,
        {
          className: 'throttle-toast dark:bg-gray-800 dark:text-gray-200',
          autoClose: 20000,
          toastId: 429,
        }
      );
    }
    return Promise.reject(error);
  }
);

const originalFetch = fetch;

// Function to get a fresh CSRF token
async function refreshCsrfToken() {
  try {
    // Make a request to a Laravel endpoint that returns a fresh CSRF token
    // You'll need to create this endpoint in your Laravel application
    const response = await originalFetch('/refresh-csrf', {
      method: 'GET',
      credentials: 'same-origin', // Important for cookies
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to refresh CSRF token');
    }

    const data = await response.json();

    // Update the meta tag with the new token
    const metaTag = document.querySelector('meta[name="csrf-token"]');
    if (metaTag) {
      metaTag.setAttribute('content', data.csrf_token);
    } else {
      // Create the meta tag if it doesn't exist
      const newMetaTag = document.createElement('meta');
      newMetaTag.name = 'csrf-token';
      newMetaTag.content = data.csrf_token;
      document.head.appendChild(newMetaTag);
    }

    return data.csrf_token;
  } catch (error) {
    console.error('Error refreshing CSRF token:', error);
    // If we can't get a new token, we'll have to reload
    window.location.reload();
    return null;
  }
}

// Keep track of if a token refresh is in progress
let refreshingToken = null;

window.fetch = async function (url, options = {}) {
  // Initialize options
  options = options || {};

  // Skip modification for external requests
  if (url.startsWith('http') && !url.includes(window.location.origin)) {
    return originalFetch(url, options);
  }

  // Initialize headers if not present
  const headers = new Headers(options.headers || {});

  // Get the current CSRF token
  let csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

  // Add CSRF token to headers if it exists
  if (csrfToken && !headers.has('X-CSRF-TOKEN')) {
    headers.append('X-CSRF-TOKEN', csrfToken);
  }

  // Add Accept header if not present
  if (!headers.has('Accept')) {
    headers.append('Accept', 'application/json');
  }

  // Update options with the modified headers
  options.headers = headers;

  try {
    // Make the original request
    const response = await originalFetch(url, options);

    // If we get a 419 (CSRF token mismatch), refresh the token and retry
    if (response.status === 419) {
      console.warn('CSRF token expired. Attempting to refresh and retry...');

      // If a refresh is already in progress, wait for it to complete
      if (refreshingToken) {
        await refreshingToken;
      } else {
        // Start a new token refresh
        refreshingToken = refreshCsrfToken();
        csrfToken = await refreshingToken;
        refreshingToken = null;

        if (!csrfToken) {
          return Promise.reject('Failed to refresh CSRF token.');
        }
      }

      // Update the token in the request headers
      headers.set('X-CSRF-TOKEN', csrfToken);
      options.headers = headers;

      // Retry the original request with the new token
      return originalFetch(url, options);
    }

    // Handle rate limiting
    if (response.status === 429) {
      // Assuming you have toast imported
      if (typeof toast !== 'undefined') {
        toast.error(
          <div>
            <b>You've reached the limit for this feature.</b>
            <br />
            To ensure fair usage, we limit access to some of our AI features for free users. You can
            try again in a few minutes, or{' '}
            <Link className="underline text-blue-500 dark:text-blue-400" href="/unlock">
              click here
            </Link>{' '}
            to explore premium plans and enjoy unlimited access.
          </div>,
          {
            className: 'throttle-toast dark:bg-gray-800 dark:text-gray-200',
            autoClose: 20000,
            toastId: 429,
          }
        );
      } else {
        console.warn('Rate limit reached (429)');
      }
    }

    return response;
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
};

let eventQueue = [];
let isProcessing = false;

// Utility function to process the queue
const processQueue = async () => {
  if (isProcessing || eventQueue.length === 0) return;

  isProcessing = true;
  const event = eventQueue.shift(); // Get the next event from the queue
  try {
    const response = await axios.post('/te', {
      page: window.location.pathname,
      metadata: event.metadata,
    });

    // Handle the response as needed
    if (response.status === 200) {
      isProcessing = false;
      if (eventQueue.length > 0) {
        // If there are more events in the queue, process the next one
        await processQueue();
      }
    } else {
      throw new Error('Failed to track event');
    }
  } catch (error) {
    console.error('Error tracking event', error);
    isProcessing = false;
    if (eventQueue.length > 0) {
      await processQueue(); // Continue processing the queue even if an error occurs
    }
  }
};

// Improved trackEvent function with queueing
window.trackEvent = (metadata = {}) => {
  // Skip tracking if user is being impersonated by an admin
  if (window.Inertia?.page?.props?.trackingDisabled) {
    console.log('Event tracking disabled during impersonation');
    return;
  }

  // Add the event to the queue
  eventQueue.push({ metadata });

  // Start processing the queue if not already processing
  if (!isProcessing) {
    processQueue();
  }
};

createInertiaApp({
  title: (title) => title,
  resolve: (name) =>
    resolvePageComponent(`./Pages/${name}.jsx`, import.meta.glob('./Pages/**/*.jsx')),
  setup({ el, App, props }) {
    // Check if the root already exists, if not, create one
    if (!el.__reactRoot) {
      el.__reactRoot = createRoot(el);
    }

    function MainApp() {
      return (
        <ErrorBoundary>
          <App {...props} />
        </ErrorBoundary>
      );
    }

    // Use the existing root instance
    el.__reactRoot.render(<MainApp />);

    // Smoothly fade out the loader with proper transition time
    const loader = document.getElementById('loading-screen');
    if (loader) {
      loader.style.opacity = '0';
      // Wait for the transition to complete before removing the element
      // The transition duration in CSS is 0.2s (200ms), so wait a bit longer to be safe
      setTimeout(() => {
        if (loader.parentNode) {
          loader.parentNode.removeChild(loader);
        }
      }, 300); // 300ms to ensure the transition completes
    }
  },
  progress: {
    color: '#4B5563',
  },
});
